import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { ActivityView } from '../components/spaces/ActivityView';
import { FilePanel } from '../components/spaces/FilePanel';
import { FolderView, type FolderViewHandlers } from '../components/spaces/FolderView';
import { RequestsView } from '../components/spaces/RequestsView';
import { SearchView, type SpaceSearchParams } from '../components/spaces/SearchView';
import { ShareDialog } from '../components/spaces/ShareDialog';
import { SpaceNav, type SpacesView } from '../components/spaces/SpaceNav';
import { TrashView } from '../components/spaces/TrashView';
import { UploadTray } from '../components/spaces/UploadTray';
import { ResizeHandle } from '../components/ui/resize-handle';
import { spaceErrorMessage, spacesApi, type SharedSpace, type SpaceNodeView, type SpaceSummary } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useResizablePanel } from '../lib/resizable';
import { useSpaceUploads } from '../lib/space-upload';
import { copyText, downloadFile, isFileDrag, useFolderListing, useFolderTree } from '../lib/spaces';

/** How long after the last confirmed upload the listing and quota are re-read — one read per burst, not per file. */
const UPLOAD_REFRESH_MS = 700;
/** …but never later than this after the first file of a burst. */
const UPLOAD_REFRESH_MAX_MS = 4_000;

/**
 * Personal spaces: each person's private folders and files, and the folders others have shared with them.
 *
 * Two panes, like the other worklists: the tree on the left (your space, then what is shared with you,
 * then Trash and Activity), and the open folder — or one of those two views — on the right. Everything
 * that selects something lives in the URL: `?folder=` is the folder open (your own root when absent),
 * `?file=` the file in the side panel, `?view=trash|activity|search|requests` the other views — the
 * last being the share requests an MCP client links its person to — and search with
 * `q`, `exact=1`, `from=<folder it began in>` and `in=1` for searching beneath that folder only — and
 * `?upload=1` is the deep link an MCP client hands out when it cannot send a file's bytes itself.
 *
 * Nothing here is part of the knowledge base: it is never ingested, and no agent reads it unless the
 * person asks their own MCP client to.
 */
export default function Spaces() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const view: SpacesView =
    viewParam === 'trash' || viewParam === 'activity' || viewParam === 'search' || viewParam === 'requests' ? viewParam : 'folder';
  const searchParams: SpaceSearchParams = {
    q: params.get('q') ?? '',
    exact: params.get('exact') === '1',
    from: params.get('from'),
    within: params.get('in') === '1',
  };
  const fileId = params.get('file');
  const uploadIntent = params.get('upload') === '1';

  const [me, setMe] = useState<SpaceSummary | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [shared, setShared] = useState<SharedSpace[] | null>(null);
  const [sharedError, setSharedError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sharing, setSharing] = useState<{ id: string; address: string } | null>(null);
  const [requestCount, setRequestCount] = useState(0);
  const [dragging, setDragging] = useState<SpaceNodeView | null>(null);

  const folderId = params.get('folder') ?? me?.rootFolderId ?? null;
  const listingState = useFolderListing(view === 'folder' ? folderId : null);
  const { listing } = listingState;
  const tree = useFolderTree();

  const loadMe = useCallback(() => {
    spacesApi
      .me()
      .then((next) => {
        setMe(next);
        setMeError(null);
      })
      .catch((err) => setMeError(spaceErrorMessage(err, 'Could not open your space')));
  }, []);

  // Only the count: the list itself is the requests view's to load. A failure costs the badge, nothing else.
  const loadRequestCount = useCallback(() => {
    spacesApi
      .shareRequests()
      .then((rows) => setRequestCount(rows.length))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadMe();
    loadRequestCount();
    spacesApi
      .shared()
      .then(setShared)
      .catch((err) => setSharedError(spaceErrorMessage(err, 'Could not load what is shared with you')));
  }, [loadMe, loadRequestCount]);

  // Your own tree starts open one level, so the first thing in the left pane is your folders.
  const rootId = me?.rootFolderId ?? null;
  useEffect(() => {
    if (rootId) tree.expand([rootId]);
  }, [rootId, tree.expand]);

  // Open the open folder's ancestors in the tree, once per folder rather than on every re-read — a
  // branch somebody collapsed by hand should stay collapsed while they work in it.
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (!listing || !folderId || revealed.current === folderId) return;
    revealed.current = folderId;
    tree.expand(listing.breadcrumbs.slice(0, -1).map((c) => c.id));
  }, [listing, folderId, tree.expand]);

  // ─── Navigation ────────────────────────────────────────────────────────────

  const navigate = useCallback(
    (update: (next: URLSearchParams) => void, options: { replace?: boolean } = {}) => {
      const next = new URLSearchParams(params);
      update(next);
      setParams(next, { replace: options.replace ?? false });
    },
    [params, setParams],
  );

  const openFolder = useCallback(
    (id: string) =>
      navigate((next) => {
        next.delete('view');
        next.delete('file');
        if (id === me?.rootFolderId) next.delete('folder');
        else next.set('folder', id);
      }),
    [navigate, me?.rootFolderId],
  );

  const openView = (next: 'trash' | 'activity' | 'requests') =>
    navigate((p) => {
      p.set('view', next);
      p.delete('file');
      p.delete('upload');
      for (const key of ['q', 'exact', 'from', 'in']) p.delete(key);
    });

  /** Opens the results; a search begun inside a folder (other than your root) can be narrowed to it there. */
  const openSearch = (q: string) =>
    navigate((p) => {
      const from = view === 'folder' ? (folderId !== me?.rootFolderId ? folderId : null) : searchParams.from;
      p.set('view', 'search');
      p.set('q', q);
      if (from) p.set('from', from);
      else p.delete('from');
      for (const key of ['file', 'upload', 'in']) p.delete(key);
    });

  const changeSearch = (next: SpaceSearchParams) =>
    navigate(
      (p) => {
        p.set('q', next.q);
        if (next.exact) p.set('exact', '1');
        else p.delete('exact');
        if (next.within && next.from) p.set('in', '1');
        else p.delete('in');
      },
      { replace: true },
    );

  const openFile = (folder: string, file: string) =>
    navigate((p) => {
      p.delete('view');
      p.set('folder', folder);
      p.set('file', file);
    });

  // ─── Keeping what is shown current ─────────────────────────────────────────

  const { reload: reloadListing } = listingState;
  const { invalidate } = tree;
  const onChanged = useCallback(
    (folderIds: Array<string | null | undefined>) => {
      reloadListing();
      invalidate(folderIds);
      loadMe();
    },
    [reloadListing, invalidate, loadMe],
  );

  // Uploads report each file as it is confirmed; a burst of them becomes one re-read. Capped, so a long
  // stream of small files still appears as it goes rather than all at once at the end.
  const pendingRefresh = useRef<{ timer: number | null; since: number; folders: Set<string> }>({ timer: null, since: 0, folders: new Set() });
  const onStored = useCallback(
    (stored: string) => {
      const pending = pendingRefresh.current;
      if (pending.folders.size === 0) pending.since = Date.now();
      pending.folders.add(stored);
      if (pending.timer !== null) window.clearTimeout(pending.timer);
      const wait = Math.max(0, Math.min(UPLOAD_REFRESH_MS, pending.since + UPLOAD_REFRESH_MAX_MS - Date.now()));
      pending.timer = window.setTimeout(() => {
        const folders = [...pending.folders];
        pending.folders.clear();
        pending.timer = null;
        onChanged(folders);
      }, wait);
    },
    [onChanged],
  );
  useEffect(() => () => {
    if (pendingRefresh.current.timer !== null) window.clearTimeout(pendingRefresh.current.timer);
  }, []);
  const uploads = useSpaceUploads(onStored);

  const flash = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice((current) => (current === message ? null : current)), 2500);
  }, []);

  const copyPath = useCallback(
    async (address: string) => {
      if (await copyText(address)) flash(`Copied ${address}`);
      else setError(`Could not copy to the clipboard. The path is ${address}`);
    },
    [flash],
  );

  // A drop names only its target; what is being moved is whatever row the drag started on.
  const draggingRef = useRef(dragging);
  draggingRef.current = dragging;
  const moveInto = useCallback(
    async (targetFolderId: string) => {
      const node = draggingRef.current;
      setDragging(null);
      if (!node || node.id === targetFolderId || node.parentId === targetFolderId) return;
      setError(null);
      try {
        if (node.type === 'folder') await spacesApi.updateFolder(node.id, { parentId: targetFolderId });
        else await spacesApi.updateFile(node.id, { folderId: targetFolderId });
        onChanged([node.parentId, targetFolderId]);
        flash(`Moved ${node.name}`);
      } catch (err) {
        setError(spaceErrorMessage(err, `Could not move ${node.name}`));
      }
    },
    [onChanged, flash],
  );

  const handlers: FolderViewHandlers = {
    onOpenFolder: openFolder,
    onOpenFile: (file) => openFile(file.parentId ?? folderId ?? '', file.id),
    onShare: setSharing,
    onDownload: (file) => {
      setError(null);
      downloadFile(file.id).catch((err) => setError(spaceErrorMessage(err, `Could not download ${file.name}`)));
    },
    onCopyPath: (address) => void copyPath(address),
    onUpload: (files) => {
      if (!me || !listing || !folderId) return;
      const crumbs = listing.breadcrumbs;
      uploads.start({
        folderId,
        destination: crumbs[crumbs.length - 1]?.address ?? '',
        files,
        maxFileBytes: me.maxFileBytes,
        atSpaceRoot: listing.space.owner && crumbs.length === 1,
      });
    },
    onMove: (target) => void moveInto(target),
    onChanged,
    onError: setError,
  };

  const panel = useResizablePanel({ initial: 280, min: 220, max: 460, side: 'left', storageKey: 'spaces.nav.width' });

  return (
    // Files dropped anywhere but the listing are swallowed rather than left to the browser, which would
    // otherwise open the file in place of the console.
    <div
      className="flex h-screen overflow-hidden bg-surface"
      onDragOver={(e) => isFileDrag(e) && e.preventDefault()}
      onDrop={(e) => isFileDrag(e) && e.preventDefault()}
    >
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        <aside style={{ width: panel.width }} className="flex-shrink-0 h-full border-r border-border bg-surface-subtle/40">
          <SpaceNav
            me={me}
            meError={meError}
            shared={shared}
            sharedError={sharedError}
            tree={tree}
            view={view}
            activeFolderId={folderId}
            onOpenFolder={openFolder}
            onOpenView={openView}
            onSearch={openSearch}
            query={view === 'search' ? searchParams.q : ''}
            dragging={dragging}
            onDropNode={(target) => void moveInto(target)}
            requestCount={requestCount}
          />
        </aside>

        <ResizeHandle handleProps={panel.handleProps} onDoubleClick={panel.reset} />

        <div className="flex-1 h-full overflow-y-auto min-w-0">
          <div className="px-7 py-7">
            {notice && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-success-ring bg-success-bg px-4 py-2 text-xs text-success-text">
                <Check size={12} />
                <span className="truncate">{notice}</span>
              </div>
            )}
            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">
                <span className="flex-1">{error}</span>
                <button type="button" onClick={() => setError(null)} aria-label="Dismiss" className="mt-0.5">
                  <X size={13} />
                </button>
              </div>
            )}

            {meError && !me ? (
              <div className="rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{meError}</div>
            ) : !me ? (
              <p className="text-xs text-ink-faint">Loading…</p>
            ) : view === 'requests' ? (
              <RequestsView
                onDecided={(decidedFolderId) => {
                  loadRequestCount();
                  // An approval changes who can see that folder: its Shared badge, wherever it is listed.
                  if (decidedFolderId) onChanged([decidedFolderId]);
                }}
              />
            ) : view === 'trash' ? (
              <TrashView trashDays={me.trashDays} onRestored={(parentId) => onChanged([parentId])} />
            ) : view === 'activity' ? (
              <ActivityView myUserId={user?.id ?? null} onOpenFolder={openFolder} onOpenFile={openFile} />
            ) : view === 'search' ? (
              <SearchView
                params={searchParams}
                onChange={changeSearch}
                // Over the results rather than instead of them, so closing the panel comes back here.
                onOpenFile={(file) => navigate((p) => p.set('file', file.id))}
              />
            ) : folderId ? (
              <FolderView
                key={folderId}
                folderId={folderId}
                me={me}
                state={listingState}
                handlers={handlers}
                dragging={dragging}
                setDragging={setDragging}
                uploadIntent={uploadIntent}
                onDismissUploadIntent={() => navigate((p) => p.delete('upload'), { replace: true })}
              />
            ) : null}
          </div>
        </div>
      </div>

      {me && fileId && (
        <FilePanel
          key={fileId}
          fileId={fileId}
          me={me}
          canEditIn={(parentId) => parentId !== null && parentId === folderId && Boolean(listing?.can.edit)}
          onClose={() => navigate((p) => p.delete('file'), { replace: true })}
          onChanged={(parentId) => onChanged([parentId])}
          onCopyPath={(address) => void copyPath(address)}
        />
      )}

      <ShareDialog
        folder={sharing}
        myUserId={user?.id ?? null}
        myEmail={me?.ownerEmail ?? user?.email ?? ''}
        onClose={() => setSharing(null)}
        onChanged={(id) => onChanged([id])}
      />

      <UploadTray uploads={uploads} />
    </div>
  );
}
